import React, { useState, useEffect, useMemo } from 'react'
import { ResponsiveContainer, PieChart as RechartsPieChart, Pie, Cell, Tooltip } from 'recharts'
import { getAllMonumentaEmployees } from '@/lib/real-data-service'

export function DepartmentChart() {
  const [employees, setEmployees] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load employees data asynchronously
  useEffect(() => {
    const loadEmployees = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const employeesData = await getAllMonumentaEmployees();
        setEmployees(employeesData);
      } catch (err) {
        console.error('Error loading employees:', err);
        setError('Falha ao carregar dados dos funcionários');
        setEmployees([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadEmployees();
  }, []);

  const data = useMemo(() => {
    if (employees.length === 0) return [];

    const departmentCounts = employees.reduce((acc, emp) => {
      const dept = emp.department || 'Não Definido'
      acc[dept] = (acc[dept] || 0) + 1
      return acc
    }, {} as Record<string, number>)

    return Object.entries(departmentCounts).map(([department, count]) => ({
      department,
      count: count as number
    }))
  }, [employees]);

  if (isLoading) {
    return (
      <div className="card">
        <h3 className="font-display text-base sm:text-lg font-semibold text-white mb-6 bg-gradient-to-r from-white to-brand-gray-light bg-clip-text text-transparent">
          Distribuição por Departamento
        </h3>
        <div className="flex justify-center items-center h-48">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-brand-primary border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
            <p className="text-brand-gray-light text-sm font-body">Carregando dados...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card">
        <h3 className="font-display text-base sm:text-lg font-semibold text-white mb-6 bg-gradient-to-r from-white to-brand-gray-light bg-clip-text text-transparent">
          Distribuição por Departamento
        </h3>
        <div className="flex justify-center items-center h-48">
          <div className="text-center">
            <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
              <span className="text-red-500 text-xl"></span>
            </div>
            <p className="text-red-400 text-sm font-body">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  const COLORS = ['#D2FA52', '#4760E0', '#D90A2C', '#10B981', '#F59E0B']

  return (
    <div className="card">
      <h3 className="font-display text-base sm:text-lg font-semibold text-white mb-6 bg-gradient-to-r from-white to-brand-gray-light bg-clip-text text-transparent">
        Distribuição por Departamento
      </h3>
      {data.length > 0 ? (
        <div className="animate-fade-in">
          <ResponsiveContainer width="100%" height={200}>
            <RechartsPieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                outerRadius={60}
                fill="#8884d8"
                dataKey="count"
              >
                {data.map((_, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </RechartsPieChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="flex justify-center items-center h-48">
          <p className="text-brand-gray-light text-sm font-body">Nenhum dado disponível</p>
        </div>
      )}
      <div className="mt-6 space-y-3">
        {data.map((item, index) => (
          <div key={item.department} className="flex items-center justify-between text-sm p-2 rounded-monumenta hover:bg-brand-dark-lighter transition-colors duration-200 group">
            <div className="flex items-center min-w-0 flex-1">
              <div 
                className="w-4 h-4 rounded-full mr-3 flex-shrink-0 shadow-sm group-hover:scale-110 transition-transform duration-200"
                style={{ backgroundColor: COLORS[index % COLORS.length] }}
              />
              <span className="text-brand-gray-light truncate font-body group-hover:text-white transition-colors duration-200">{item.department}</span>
            </div>
            <span className="text-white font-display font-semibold ml-2 group-hover:text-brand-primary transition-colors duration-200">{item.count}</span>
          </div>
        ))}
      </div>
    </div>
  )
}